import { Injectable } from "@angular/core";

import { Observable } from "rxjs";

import { Organization } from "@shared/models/organization.model";
import { PaginatedResponse, QueryParams } from "@shared/models/query-params.model";
import { BaseHttpService } from "@shared/services/base-http.service";

export interface CreateOrganizationDto {
  name: string;
  description?: string;
  planId: string;
  apiEnabled?: boolean;
}

export interface UpdateOrganizationDto {
  id: string;
  name?: string;
  description?: string;
  planId?: string;
  apiEnabled?: boolean;
}

import { UserRole } from "@shared/models/user.model";

export interface InviteUserDto {
  email: string;
  role: UserRole;
}

export interface InviteUsersDto {
  users: InviteUserDto[];
}

@Injectable({
  providedIn: "root"
})
export class OrganizationHttpService extends BaseHttpService {
  protected override baseEndpoint = "organizations";

  /**
   * Get an organization by ID
   * @param organizationId The ID of the organization to get
   * @returns An Observable that emits the organization
   */
  getById(organizationId: string): Observable<Organization> {
    return this.get<Organization>({
      endpoint: organizationId,
      type: Organization
    });
  }

  /**
   * Get all organizations with support for pagination, sorting, and filtering
   * @param queryParams Optional query parameters for pagination, sorting, and filtering
   * @returns Paginated organizations
   */
  getAll(queryParams?: QueryParams): Observable<PaginatedResponse<Organization>> {
    return this.getMany<Organization>({
      queryParams,
      type: Organization
    });
  }

  /**
   * Create a new organization
   * @param data The data for the new organization
   * @returns An Observable that emits the created organization
   */
  create(data: CreateOrganizationDto): Observable<Organization> {
    return this.post<Organization>({
      body: data,
      type: Organization
    });
  }

  /**
   * Update an organization
   * @param data The data for the updated organization
   * @returns An Observable that emits the updated organization
   */
  update(data: UpdateOrganizationDto): Observable<Organization> {
    const { id, ...body } = data;
    return this.patch<Organization>({
      endpoint: `${id}`,
      body: body,
      type: Organization
    });
  }

  /**
   * Delete an organization by ID
   * @param organizationId The ID of the organization to delete
   * @returns An Observable that emits a void response
   */
  deleteOrganization(organizationId: string): Observable<void> {
    return this.delete<void>({
      endpoint: organizationId
    });
  }

  /**
   * Suspend an organization by ID
   * @param organizationId The ID of the organization to suspend
   * @returns An Observable that emits the suspended organization
   */
  suspend(organizationId: string): Observable<Organization> {
    return this.patch<Organization>({
      endpoint: `${organizationId}/suspend`
    });
  }

  /**
   * Unsuspend an organization by ID
   * @param organizationId The ID of the organization to unsuspend
   * @returns An Observable that emits the unsuspended organization
   */
  unsuspend(organizationId: string): Observable<Organization> {
    return this.patch<Organization>({
      endpoint: `${organizationId}/unsuspend`
    });
  }

  /**
   * Invite users to an organization
   * @param organizationId The organization ID
   * @param users List of users to invite
   * @returns An Observable with results
   */
  inviteUsers(
    organizationId: string,
    users: InviteUserDto[]
  ): Observable<{ results: { email: string; success: boolean; message?: string }[] }> {
    return this.post<{ results: { email: string; success: boolean; message?: string }[] }>({
      endpoint: `${organizationId}/invite`,
      body: { users }
    });
  }

  /**
   * Resend an invite to a user
   * @param organizationId The organization ID
   * @param userId The user ID to resend invite to
   * @returns An Observable with the result
   */
  resendInvite(organizationId: string, userId: string): Observable<{ success: boolean; message?: string }> {
    return this.post<{ success: boolean; message?: string }>({
      endpoint: `${organizationId}/invites/${userId}/resend`,
      body: {}
    });
  }

  getMembersCount(): Observable<{ count: number }> {
    return this.get<{ count: number }>({
      endpoint: "members/count"
    });
  }
}
